import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { track, trackScroll } from "@/lib/track";

const MILESTONES = [25, 50, 75, 100];

export const usePageViewTracking = () => {
  const location = useLocation();
  const firedRef = useRef<Set<number>>(new Set());

  // Page view per route change
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    firedRef.current = new Set();
    let event_type = "page_viewed";
    if (location.pathname.startsWith("/services/")) event_type = "service_page_viewed";
    else if (location.pathname === "/book") event_type = "booking_page_viewed";
    else if (location.pathname.startsWith("/blog/")) event_type = "blog_article_viewed";
    track(event_type, { path: location.pathname, metadata: { title: document.title } });
  }, [location.pathname]);

  // Scroll milestones
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      if (total <= 0) return;
      const pct = Math.round((h.scrollTop / total) * 100);
      for (const m of MILESTONES) {
        if (pct >= m && !firedRef.current.has(m)) {
          firedRef.current.add(m);
          trackScroll(m);
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  // Global click delegation: phone, book CTA, FAQ accordions
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest("a") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href") || "";
        const text = (anchor.textContent || "").trim().slice(0, 100);
        if (href.startsWith("tel:")) {
          track("call_link_clicked", { label: text, metadata: { href, placement: anchor.dataset.placement || "unknown" } });
        } else if (href.startsWith("mailto:")) {
          track("email_link_clicked", { label: text, metadata: { href } });
        } else if (/^\/book(\b|\/|$)/.test(href) || /book[^a-z]*(call|appointment)/i.test(text)) {
          track("book_call_clicked", { label: text, metadata: { href, placement: anchor.dataset.placement || "unknown" } });
        }
      }
      // FAQ <summary> toggles
      const summary = target.closest("summary") as HTMLElement | null;
      if (summary) {
        const details = summary.parentElement as HTMLDetailsElement | null;
        const opening = !details?.open;
        track(opening ? "faq_opened" : "faq_closed", { label: (summary.textContent || "").trim().slice(0, 200) });
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [location.pathname]);
};
